import { useCallback, useRef, useState } from "react";

type UseAudioSamplesResult = {
  activeSampleKey: string | null;
  playSample: (key: string, audio: HTMLAudioElement) => void;
  stopSample: (key?: string) => void;
};

export const useAudioSamples = (): UseAudioSamplesResult => {
  const [activeSampleKey, setActiveSampleKey] = useState<string | null>(null);
  const activeSampleKeyRef = useRef<string | null>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const detachListeners = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  const stopSample = useCallback(
    (key?: string) => {
      if (!activeAudioRef.current) {
        return;
      }

      if (key && activeSampleKeyRef.current !== key) {
        return;
      }

      const audio = activeAudioRef.current;
      detachListeners();
      activeAudioRef.current = null;
      activeSampleKeyRef.current = null;
      setActiveSampleKey(null);

      if (!audio.paused) {
        audio.pause();
      }
      audio.currentTime = 0;
    },
    [detachListeners],
  );

  const playSample = useCallback(
    (key: string, audio: HTMLAudioElement) => {
      if (!audio) {
        return;
      }

      if (activeAudioRef.current && activeAudioRef.current !== audio) {
        stopSample();
      }

      detachListeners();

      const handlePauseOrEnded = () => {
        if (activeAudioRef.current === audio) {
          detachListeners();
          activeAudioRef.current = null;
          activeSampleKeyRef.current = null;
          setActiveSampleKey(null);
        }
      };

      audio.addEventListener("pause", handlePauseOrEnded);
      audio.addEventListener("ended", handlePauseOrEnded);

      cleanupRef.current = () => {
        audio.removeEventListener("pause", handlePauseOrEnded);
        audio.removeEventListener("ended", handlePauseOrEnded);
      };

      activeAudioRef.current = audio;
      activeSampleKeyRef.current = key;
      setActiveSampleKey(key);
    },
    [detachListeners, stopSample],
  );

  return {
    activeSampleKey,
    playSample,
    stopSample,
  };
};
